import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../Button/Button';
import CloseButton from '../CloseButton/CloseButton';
import TimeInput from '../TimeInput/TimeInput';
import styles from './ChaptersEditor.module.scss';
import { useChaptersContext } from '../../context/ChaptersContext';
import ChapterItem from './ChapterItem';
import { ENTER_KEY_CODE } from '../../constants/common';

const MIN_TITLE_LENGTH = 1;
const MIN_CHAPTER_STEP = 1000;

const ChaptersEditor = ({
  duration,
  onSave,
  miniSidebar,
  setMiniSidebar,
  checkClosing,
  onCloseEditor,
}) => {
  const [newChapterTitle, setNewChapterTitle] = useState('');

  const [loading, setLoading] = useState(false);
  const [isTimeValid, setIsTimeValid] = useState(false);
  const { t } = useTranslation();
  const nameRef = useRef(null);

  const {
    newChapterStart,
    closeChaptersEditor,
    updateChapterStartFromEditor,
    chapters,
    handleAddChapter,
    hasChanged,
  } = useChaptersContext();

  const isTitleValid = useMemo(() => newChapterTitle?.trim().length >= MIN_TITLE_LENGTH, [newChapterTitle]);

  useEffect(() => {
    const chapterStartInMs = newChapterStart * 1000;
    const chaptersBefore = chapters?.filter(item => item.startTime <= chapterStartInMs);
    const chaptersAfter = chapters?.filter(item => item.startTime > chapterStartInMs);
    const previousChapterTime = chaptersBefore[chaptersBefore.length - 1]?.startTime || 0;
    const nextChapterTime = chaptersAfter[0]?.startTime ?? duration;

    let valid = true;

    if ((chaptersBefore.length && previousChapterTime === chapterStartInMs) || nextChapterTime === chapterStartInMs) {
      valid = false;
    } else if (
      chaptersBefore.length &&
      chapterStartInMs > previousChapterTime &&
      chapterStartInMs < previousChapterTime + MIN_CHAPTER_STEP
    ) {
      valid = false;
    } else if (chapterStartInMs < nextChapterTime && chapterStartInMs > nextChapterTime - MIN_CHAPTER_STEP) {
      valid = false;
    }
    setIsTimeValid(valid);
  }, [chapters, newChapterStart]);

  useEffect(() => {
    if (checkClosing && !miniSidebar) {
      handleCloseEditor();
    }
  }, [checkClosing]);

  const onAddChapter = () => {
    handleAddChapter(newChapterTitle);
    setNewChapterTitle('');
  };

  const handleTimeStampChange = value => {
    updateChapterStartFromEditor(value);
  };

  const handleNameChange = e => {
    setNewChapterTitle(e.target.value);
  };

  const handleSaveOnEnter = useCallback(
    e => {
      e.keyCode === ENTER_KEY_CODE && isTitleValid && onAddChapter();
    },
    [isTitleValid, onAddChapter, newChapterTitle]
  );

  const handleSaveChapters = useCallback(async () => {
    setLoading(true);
    try {
      await onSave(chapters);
      closeChaptersEditor();
    } catch (e) {
      console.log('Error while saving', e);
    } finally {
      setLoading(false);
    }
  }, [chapters]);

  const handleCloseEditor = useCallback(() => {
    if (hasChanged) {
      const confirmClose = confirm('Are you sure you want to close the editor? Changes that you made may not be saved');
      if (!confirmClose) {
        return;
      }
    }
    setMiniSidebar(false);
    closeChaptersEditor();
    onCloseEditor();
  }, [hasChanged]);

  const onReloadEvent = event => {
    const e = event || window.event;
    // Cancel the event
    e.preventDefault();
    if (e) {
      e.returnValue = ''; // Legacy method for cross browser support
    }
    return ''; // Legacy method for cross browser support
  };

  useEffect(() => {
    if (hasChanged) {
      window.addEventListener('beforeunload', onReloadEvent);
    } else {
      window.removeEventListener('beforeunload', onReloadEvent);
    }

    return () => window.removeEventListener('beforeunload', onReloadEvent);
  }, [hasChanged]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2>{t('my_video.tools.chapters')}</h2>
        <CloseButton onClose={handleCloseEditor} />
      </div>
      <div className={styles.newChapterContainer}>
        <TimeInput
          value={newChapterStart}
          onChange={handleTimeStampChange}
          inputClassName={styles.timestampInput}
          min={0}
          max={duration / 1000}
        />
        <input
          type="text"
          className={styles.nameInput}
          ref={nameRef}
          value={newChapterTitle}
          placeholder={t('chapters_editor.placeholder')}
          onChange={handleNameChange}
          onKeyDown={handleSaveOnEnter}
          maxLength={70}
        />
        <CustomButton
          variant="link"
          onClick={onAddChapter}
          label={t('general.add')}
          className={styles.itemButton}
          disabled={!isTitleValid || !isTimeValid}
        />
      </div>
      <ul className={styles.chaptersList}>
        {chapters?.map((item, index) => (
          <ChapterItem
            key={item.id}
            id={item.id}
            title={item.title}
            startTime={item.startTime}
            min={chapters[index - 1] ? (chapters[index - 1].startTime + MIN_CHAPTER_STEP) / 1000 : 0}
            max={index === chapters.length - 1 ? duration / 1000 : (chapters[index + 1].startTime - MIN_CHAPTER_STEP) / 1000}
          />
        ))}
      </ul>
      <CustomButton
        label={t('general.save')}
        variant="primary"
        onClick={handleSaveChapters}
        className={styles.saveBtn}
        disabled={!hasChanged}
        loading={loading}
        loadingSpinner
      />
    </div>
  );
};

export default ChaptersEditor;
